/**
 * The frame a project was left on. Kept as a JPEG blob in its own IndexedDB database —
 * a frame is far too large for the localStorage budget the scene snapshots live in, and
 * losing one costs nothing: the card falls back to the drawn 2D plan.
 *
 * The store is a module singleton because the editor writes it while leaving and the
 * workspace reads it a paint later; subscribers get the new frame when it lands.
 */
import type { PreviewKind } from './projectView';

export interface StoredPreview {
  id: string;
  blob: Blob;
  kind: PreviewKind;
  width: number;
  height: number;
  savedAt: number;
}

export interface PreviewBackend {
  get(id: string): Promise<StoredPreview | undefined>;
  put(preview: StoredPreview): Promise<void>;
  delete(id: string): Promise<void>;
}

export class MemoryPreviews implements PreviewBackend {
  private items = new Map<string, StoredPreview>();
  async get(id: string) { return this.items.get(id); }
  async put(preview: StoredPreview) { this.items.set(preview.id, { ...preview }); }
  async delete(id: string) { this.items.delete(id); }
}

const DB = 'hp-previews', STORE = 'frames';

export class IndexedDbPreviews implements PreviewBackend {
  private db: Promise<IDBDatabase>;
  constructor() {
    this.db = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB, 1);
      request.onupgradeneeded = () => { const db = request.result; if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'id' }); };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error('IndexedDB unavailable'));
    });
  }
  private async run<T>(mode: IDBTransactionMode, work: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
    const db = await this.db;
    return new Promise((resolve, reject) => {
      const request = work(db.transaction(STORE, mode).objectStore(STORE));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
    });
  }
  get(id: string) { return this.run<StoredPreview | undefined>('readonly', (store) => store.get(id)); }
  async put(preview: StoredPreview) { await this.run('readwrite', (store) => store.put(preview)); }
  async delete(id: string) { await this.run('readwrite', (store) => store.delete(id)); }
}

type Listener = (projectId: string) => void;

export class PreviewStore {
  private items = new Map<string, StoredPreview | null>();
  private pending = new Map<string, Promise<StoredPreview | null>>();
  private urls = new Map<string, string>();
  private listeners = new Set<Listener>();

  constructor(private readonly backend: PreviewBackend) {}

  /** Read a project's frame once; later calls answer from memory. */
  request(projectId: string): Promise<StoredPreview | null> {
    if (this.items.has(projectId)) return Promise.resolve(this.items.get(projectId) ?? null);
    let pending = this.pending.get(projectId);
    if (!pending) {
      pending = this.backend.get(projectId)
        .then((preview) => preview ?? null)
        .catch(() => null)
        .then((preview) => {
          this.pending.delete(projectId);
          // A save or a removal that landed while the read was on its way is newer than what the read found:
          // IndexedDB answers a read asked before a write with the record from before it. The frame of a leave
          // encoded during the card's first read, or a copy saved during the new card's, stays.
          if (this.items.has(projectId)) return this.items.get(projectId) ?? null;
          this.items.set(projectId, preview);
          this.emit(projectId);
          return preview;
        });
      this.pending.set(projectId, pending);
    }
    return pending;
  }

  /** What has already been read: null when absent, undefined while it is still unknown. */
  peek(projectId: string): StoredPreview | null | undefined { return this.items.get(projectId); }

  /** A displayable URL for a read frame, kept until the frame is replaced or removed. */
  url(projectId: string): string | undefined {
    const preview = this.items.get(projectId);
    if (!preview || typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') return undefined;
    let url = this.urls.get(projectId);
    if (!url) { url = URL.createObjectURL(preview.blob); this.urls.set(projectId, url); }
    return url;
  }

  async save(projectId: string, blob: Blob, kind: PreviewKind, size: { width: number; height: number }): Promise<boolean> {
    const preview: StoredPreview = { id: projectId, blob, kind, width: size.width, height: size.height, savedAt: Date.now() };
    this.forgetUrl(projectId);
    this.items.set(projectId, preview);
    this.emit(projectId);
    try { await this.backend.put(preview); return true; } catch (error) { console.warn('Project preview not saved:', error); return false; }
  }

  async remove(projectId: string): Promise<void> {
    this.forgetUrl(projectId);
    this.items.set(projectId, null);
    this.emit(projectId);
    await this.backend.delete(projectId).catch(() => { /* the card already shows the plan */ });
  }

  /** A duplicated project keeps the original's frame until it is left on one of its own. */
  async copy(fromId: string, toId: string): Promise<void> {
    const preview = await this.request(fromId);
    if (preview) await this.save(toId, preview.blob, preview.kind, preview);
  }

  subscribe(listener: Listener): () => void { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; }

  private emit(projectId: string): void { for (const listener of this.listeners) listener(projectId); }
  private forgetUrl(projectId: string): void {
    const url = this.urls.get(projectId);
    if (!url) return;
    this.urls.delete(projectId);
    if (typeof URL?.revokeObjectURL === 'function') URL.revokeObjectURL(url);
  }
}

/**
 * A canvas as a card-sized JPEG: drawn over the stage colour, because both canvases are
 * transparent where the app's own background shows through.
 */
export function canvasPreviewBlob(source: HTMLCanvasElement, maxSide: number, background: string, quality = 0.72): Promise<Blob | null> {
  if (!source.width || !source.height) return Promise.resolve(null);
  const size = previewSize(source, maxSide);
  const target = document.createElement('canvas');
  target.width = size.width;
  target.height = size.height;
  const context = target.getContext('2d');
  if (!context) return Promise.resolve(null);
  context.fillStyle = background;
  context.fillRect(0, 0, target.width, target.height);
  context.drawImage(source, 0, 0, source.width, source.height, 0, 0, target.width, target.height);
  return new Promise((resolve) => target.toBlob((blob) => resolve(blob), 'image/jpeg', quality));
}

/** The long side of a stored frame: enough for a retina project card, small enough to keep. */
export const PREVIEW_MAX_SIDE = 720;

/** The size a canvas is stored at — the same reduction both capture paths apply. */
export function previewSize(source: { width: number; height: number }, maxSide = PREVIEW_MAX_SIDE): { width: number; height: number } {
  const scale = Math.min(1, maxSide / Math.max(source.width, source.height, 1));
  return { width: Math.max(1, Math.round(source.width * scale)), height: Math.max(1, Math.round(source.height * scale)) };
}

const backend: PreviewBackend = typeof indexedDB === 'undefined' ? new MemoryPreviews() : new IndexedDbPreviews();
export const projectPreviews = new PreviewStore(backend);
